import React, { useState, useEffect, useRef } from 'react';

interface StatItem {
  number: string;
  label: string;
  icon: React.ReactElement;
  color: string;
  animatedNumber?: number;
  suffix?: string;
}

const Stats: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [animatedNumbers, setAnimatedNumbers] = useState<number[]>([0, 0, 0, 0]);
  const sectionRef = useRef<HTMLDivElement>(null);

  const stats: StatItem[] = [
    { 
      number: '5,000+',
      label: 'Websites Hosted', 
      icon: <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9-3-9m-9 9a9 9 0 919-9" /></svg>,
      color: 'from-blue-500 to-cyan-500',
      animatedNumber: 5000,
      suffix: '+'
    },
    { 
      number: '99.99%', 
      label: 'Uptime Guarantee', 
      icon: <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
      color: 'from-yellow-500 to-orange-500',
      animatedNumber: 99.99,
      suffix: '%'
    },
    { 
      number: '24/7', 
      label: 'Expert Support', 
      icon: <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
      color: 'from-purple-500 to-violet-500',
      animatedNumber: 24,
      suffix: '/7'
    },
    { 
      number: '15+', 
      label: 'Years Experience', 
      icon: <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
      color: 'from-orange-500 to-red-500',
      animatedNumber: 15,
      suffix: '+'
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          startCountAnimation();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const startCountAnimation = () => {
    stats.forEach((stat, index) => {
      if (stat.animatedNumber) {
        animateNumber(index, stat.animatedNumber);
      }
    });
  };

  const animateNumber = (index: number, target: number) => {
    const duration = 2000;
    const increment = target / (duration / 16);
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        current = target;
        clearInterval(timer);
      }
      
      setAnimatedNumbers(prev => {
        const newNumbers = [...prev];
        newNumbers[index] = current;
        return newNumbers;
      });
    }, 16);
  };

  const formatNumber = (num: number, index: number) => {
    const stat = stats[index];
    if (index === 1) { // Uptime percentage
      return num.toFixed(2);
    }
    if (index === 0) { // Websites hosted
      return Math.floor(num).toLocaleString();
    }
    return Math.floor(num).toString();
  };

  return (
    <section 
      ref={sectionRef}
      className="bg-white py-20"
    >

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-full">
            <span className="w-2 h-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full mr-3"></span>
            <span className="text-sm font-semibold text-gray-700">Trusted Worldwide</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
            Numbers That Speak For Themselves
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Join thousands of satisfied customers who trust our platform for their digital presence
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`group relative transform transition-all duration-700 hover:-translate-y-2 ${
                isVisible 
                  ? 'translate-y-0 opacity-100' 
                  : 'translate-y-8 opacity-0'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Main Card */}
              <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 text-center relative overflow-hidden group">
                {/* Glossy overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                <div className="relative z-10">
                {/* Icon */}
                <div className={`inline-flex items-center justify-center w-16 h-16 ${index === 1 ? 'bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600' : 'bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900'} rounded-2xl mb-6 shadow-lg relative overflow-hidden`}>
                  <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                  <span className="text-white text-2xl relative z-10">{stat.icon}</span>
                </div>

                {/* Animated Number */}
                <div className="mb-4">
                  <div className="text-4xl md:text-5xl font-black text-primary-navy">
                    {isVisible ? (
                      <>
                        {formatNumber(animatedNumbers[index], index)}
                        {stat.suffix}
                      </>
                    ) : (
                      '0'
                    )}
                  </div>
                </div>

                {/* Label */}
                <div className="text-gray-600 font-semibold text-lg">
                  {stat.label}
                </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center space-x-4 text-gray-600">
            <span className="text-sm font-medium">Ready to join them?</span>
            <div className="flex space-x-1">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              ))}
            </div>
            <span className="text-sm font-semibold">4.9/5 rating</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Stats;